import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Play, Pause, ChevronLeft, ChevronRight, Maximize, Minimize, Music, VolumeX } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const buildUrl = (photo) => {
  if (!photo || !photo.file_path) return '';
  const backendUrl = getBackendUrl();
  let path = photo.file_path;
  if (!path.startsWith('/api')) path = `/api/uploads/photos/${path}`;
  const sep = path.includes('?') ? '&' : '?';
  return `${backendUrl}${path}${sep}v=${photo.id}`;
};

export default function Slideshow({ photos, settings, open, onClose }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [fadeKey, setFadeKey] = useState(0);
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [playingSong, setPlayingSong] = useState(null); // {video_id, title}
  const [library, setLibrary] = useState(null); // null = not loaded yet
  const [ytReady, setYtReady] = useState(false);
  const ytPlayerRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  // Setup YouTube IFrame Player API (needed so play is triggered inside the tap gesture — required on iOS)
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const setup = () => {
      if (cancelled || ytPlayerRef.current || !document.getElementById('slideshow-yt-player')) return;
      ytPlayerRef.current = new window.YT.Player('slideshow-yt-player', {
        width: '1',
        height: '1',
        playerVars: { playsinline: 1, controls: 0 },
        events: {
          onReady: () => { if (!cancelled) setYtReady(true); },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              e.target.seekTo(0);
              e.target.playVideo();
            }
          },
        },
      });
    };
    if (window.YT && window.YT.Player) {
      setup();
    } else {
      const prevCb = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prevCb) prevCb();
        setup();
      };
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
    }
    return () => {
      cancelled = true;
      try { ytPlayerRef.current?.destroy?.(); } catch (e) { /* noop */ }
      ytPlayerRef.current = null;
      setYtReady(false);
    };
  }, [open]);

  const playSong = (song) => {
    const p = ytPlayerRef.current;
    if (p && ytReady && typeof p.loadVideoById === 'function') {
      p.loadVideoById(song.video_id);
      if (p.unMute) p.unMute();
      if (p.setVolume) p.setVolume(100);
      if (p.playVideo) p.playVideo();
    }
    setPlayingSong(song);
    setMusicPickerOpen(false);
  };

  const stopSong = () => {
    try { ytPlayerRef.current?.stopVideo?.(); } catch (e) { /* noop */ }
    setPlayingSong(null);
    setMusicPickerOpen(false);
  };

  const adminPickId = settings?.music_video_id;

  const openMusicPicker = async () => {
    setMusicPickerOpen((o) => !o);
    if (library === null) {
      try {
        const r = await axios.get(`${getBackendUrl()}/api/slideshow/music-library`);
        setLibrary(r.data.videos || []);
      } catch (e) {
        setLibrary([]);
      }
    }
  };

  const songs = React.useMemo(() => {
    const list = [...(library || [])];
    if (adminPickId && !list.some((s) => s.video_id === adminPickId)) {
      list.unshift({ video_id: adminPickId, title: "Featured song" });
    }
    return list;
  }, [library, adminPickId]);

  const duration = Math.max(2, Math.min(settings?.duration_seconds || 5, 60));
  const transition = settings?.transition || 'fade';
  const showCaptions = settings?.show_captions !== false;
  const sortOrder = settings?.sort_order || 'asc';

  // Sort photos by date
  const sortedPhotos = React.useMemo(() => {
    const list = [...(photos || [])];
    list.sort((a, b) => {
      const ta = new Date(a.upload_date || a.created_at || a.timestamp || 0).getTime();
      const tb = new Date(b.upload_date || b.created_at || b.timestamp || 0).getTime();
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    });
    return list;
  }, [photos, sortOrder]);

  const next = useCallback(() => {
    setIndex((i) => (sortedPhotos.length === 0 ? 0 : (i + 1) % sortedPhotos.length));
    setFadeKey((k) => k + 1);
  }, [sortedPhotos.length]);

  const prev = useCallback(() => {
    setIndex((i) => (sortedPhotos.length === 0 ? 0 : (i - 1 + sortedPhotos.length) % sortedPhotos.length));
    setFadeKey((k) => k + 1);
  }, [sortedPhotos.length]);

  // Auto-advance
  useEffect(() => {
    if (!open || !playing || sortedPhotos.length <= 1) return;
    timerRef.current = setTimeout(next, duration * 1000);
    return () => clearTimeout(timerRef.current);
  }, [open, playing, index, duration, next, sortedPhotos.length]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setIndex(0);
      setPlaying(true);
      setFadeKey(0);
      setPlayingSong(null);
      setMusicPickerOpen(false);
    }
  }, [open]);

  // Keyboard controls
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
      else if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, prev, onClose]);

  // Fullscreen state listener
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  if (!open) return null;
  if (sortedPhotos.length === 0) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center" data-testid="slideshow-empty">
        <div className="text-white text-center">
          <p className="mb-4">No photos to display</p>
          <button onClick={onClose} className="px-4 py-2 bg-white/20 rounded-lg text-white">Close</button>
        </div>
      </div>
    );
  }

  const photo = sortedPhotos[index];
  const url = buildUrl(photo);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center select-none"
      data-testid="slideshow-viewer"
    >
      {/* Image */}
      <img
        key={fadeKey}
        src={url}
        alt={photo?.description || 'slide'}
        className={`max-w-full max-h-full object-contain ${transition === 'fade' ? 'animate-slide-fade' : ''}`}
        style={{ animationDuration: '600ms' }}
      />

      {/* Caption */}
      {showCaptions && photo?.description && (
        <div className="absolute bottom-20 left-0 right-0 px-6 text-center pointer-events-none">
          <p className="inline-block bg-black/60 text-white text-sm sm:text-base px-4 py-2 rounded-lg max-w-[90vw]">
            {photo.description}
          </p>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent z-20">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-white text-xs font-semibold tracking-wide flex-shrink-0">
            {index + 1} / {sortedPhotos.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openMusicPicker}
            className={`p-2 rounded-full text-white ${playingSong ? 'bg-green-500/80 hover:bg-green-500' : 'bg-white/10 hover:bg-white/20'}`}
            title="Music"
            data-testid="slideshow-music-btn"
          >
            <Music className="w-4 h-4" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            data-testid="slideshow-fullscreen-btn"
          >
            {fullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            title="Close"
            data-testid="slideshow-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/60 rounded-full px-4 py-2 backdrop-blur-sm">
        <button
          onClick={prev}
          className="p-2 rounded-full hover:bg-white/15 text-white"
          aria-label="Previous"
          data-testid="slideshow-prev-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => setPlaying((p) => !p)}
          className="p-2 rounded-full bg-white text-black hover:bg-white/90"
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid="slideshow-playpause-btn"
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <button
          onClick={next}
          className="p-2 rounded-full hover:bg-white/15 text-white"
          aria-label="Next"
          data-testid="slideshow-next-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Music picker panel */}
      {musicPickerOpen && (
        <div
          className="absolute top-14 right-3 z-30 w-72 max-w-[85vw] bg-black/85 backdrop-blur-md rounded-xl border border-white/15 overflow-hidden"
          data-testid="slideshow-music-picker"
        >
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
            <p className="text-white text-sm font-semibold flex items-center gap-2">
              <Music className="w-4 h-4 text-green-400" />
              Wally's World Music
            </p>
            <button onClick={() => setMusicPickerOpen(false)} className="text-white/60 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {library === null ? (
              <p className="text-white/60 text-xs p-4 text-center">Loading songs...</p>
            ) : songs.length === 0 ? (
              <p className="text-white/60 text-xs p-4 text-center">No songs available right now.</p>
            ) : (
              songs.map((song) => {
                const isPlaying = playingSong?.video_id === song.video_id;
                return (
                  <button
                    key={song.video_id}
                    onClick={() => (isPlaying ? stopSong() : playSong(song))}
                    className={`w-full text-left px-4 py-2.5 text-sm border-b border-white/5 transition-colors ${
                      isPlaying ? 'bg-green-500/20 text-green-300' : 'text-white/85 hover:bg-white/10'
                    }`}
                    data-testid={`music-song-${song.video_id}`}
                  >
                    <span className="line-clamp-2">{isPlaying ? '● ' : ''}{song.title}</span>
                  </button>
                );
              })
            )}
          </div>
          {playingSong && (
            <button
              onClick={stopSong}
              className="w-full px-4 py-2.5 text-sm font-semibold text-red-300 hover:bg-red-500/10 flex items-center gap-2 justify-center border-t border-white/10"
              data-testid="stop-music-btn"
            >
              <VolumeX className="w-4 h-4" />
              Stop Music
            </button>
          )}
        </div>
      )}

      {/* Now playing chip */}
      {playingSong && !musicPickerOpen && (
        <div className="absolute top-14 right-3 z-20 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5 max-w-[70vw] pointer-events-none">
          <p className="text-green-300 text-xs truncate">♪ {playingSong.title}</p>
        </div>
      )}

      {/* YouTube music player (API-controlled; fallback iframe if API not ready) */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: 1, height: 1, overflow: 'hidden', opacity: 0.01, pointerEvents: 'none' }}>
        <div id="slideshow-yt-player" data-testid="slideshow-yt-player" />
      </div>
      {playingSong && !ytReady && (
        <iframe
          title="slideshow-music"
          src={`https://www.youtube.com/embed/${playingSong.video_id}?autoplay=1&loop=1&playlist=${playingSong.video_id}&controls=0&playsinline=1`}
          allow="autoplay; encrypted-media"
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0 }}
          data-testid="slideshow-music-iframe"
        />
      )}

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
        {playing && (
          <div
            key={`bar-${fadeKey}`}
            className="h-full bg-white"
            style={{
              animation: `slideshow-progress ${duration}s linear forwards`,
            }}
          />
        )}
      </div>

      {/* Local CSS for animations */}
      <style>{`
        @keyframes slideshow-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes slide-fade {
          from { opacity: 0; transform: scale(1.02); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-slide-fade {
          animation-name: slide-fade;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}
